import { matchWorkspaceLocation } from "@/routing/core/locations";
import type { AnalyticsGlobalProperties } from "./events";

export function getAnalyticsRouteFamily(pathname: string): string {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/auth")) return "auth";
  if (pathname.startsWith("/error")) return "error";
  if (pathname.startsWith("/settings")) return "settings";

  const workspace = matchWorkspaceLocation(pathname);
  if (workspace) return workspace.kind;

  return "app";
}

export function getAnalyticsPathTemplate(pathname: string): string {
  const workspace = matchWorkspaceLocation(pathname);
  if (workspace?.kind === "project") return "/project/$projectId/$";
  if (workspace?.kind === "admin") return `/df-admin${workspace.currentPath}`;
  return pathname || "/";
}

export function getAnalyticsSubView(pathname: string): string | undefined {
  const workspace = matchWorkspaceLocation(pathname);
  if (!workspace || workspace.kind !== "project") return undefined;

  const [firstSegment, secondSegment] = workspace.currentPath.split("/").filter(Boolean);
  if (!firstSegment) return "overview";
  if (firstSegment === "entity" && secondSegment) return "entity";
  return firstSegment;
}

export function getWorkspaceAnalyticsContext(pathname: string): {
  route_family: string;
  workspace_kind?: "project" | "admin";
  project_id?: string;
  path_template: string;
  sub_view?: string;
} {
  const workspace = matchWorkspaceLocation(pathname);
  return {
    route_family: getAnalyticsRouteFamily(pathname),
    workspace_kind: workspace?.kind,
    project_id: workspace?.kind === "project" ? (workspace.resourceId ?? undefined) : undefined,
    path_template: getAnalyticsPathTemplate(pathname),
    sub_view: getAnalyticsSubView(pathname),
  };
}

export function createAnalyticsGlobalProperties(args: {
  environment: string;
  release: string;
  orgId?: string | null;
  pathname?: string;
}): AnalyticsGlobalProperties {
  const routeContext = args.pathname
    ? getWorkspaceAnalyticsContext(args.pathname)
    : {
        route_family: undefined,
        workspace_kind: undefined,
        project_id: undefined,
        sub_view: undefined,
      };

  return {
    app: "web_app",
    app_area: "product",
    environment: args.environment,
    release: args.release,
    route_family: routeContext.route_family,
    workspace_kind: routeContext.workspace_kind,
    org_id: args.orgId ?? null,
    project_id: routeContext.project_id ?? null,
    sub_view: routeContext.sub_view ?? null,
  };
}

export function normalizeClaudiaSurface(
  surfaceId: string | null | undefined
): "full_screen" | "right_panel" | "editor_inline" | undefined {
  if (surfaceId === "chat-ui") return "full_screen";
  if (surfaceId === "sidebar-panel") return "right_panel";
  if (surfaceId === "editor-inline") return "editor_inline";
  return undefined;
}

export function bucketDurationMs(durationMs: number | null | undefined): string | undefined {
  if (durationMs == null || !Number.isFinite(durationMs)) return undefined;
  if (durationMs < 5_000) return "lt_5s";
  if (durationMs < 15_000) return "5s_15s";
  if (durationMs < 60_000) return "15s_60s";
  return "gte_60s";
}
